import { useState } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { Bell, Menu, X, Search, Sparkles, Calculator, ClipboardCheck, ListChecks, BookOpen, Building2, ExternalLink, User, LogOut, Heart, Newspaper, MessageSquare } from 'lucide-react'
import Logo from '@/components/common/Logo'
import { useAuthStore } from '@/stores/authStore'
import { useUnreadCount } from '@/features/notification/api'
import { ROUTES } from '@/lib/constants'
import { motion, AnimatePresence } from 'framer-motion'

export default function MobileHeader() {
  const navigate = useNavigate()
  const location = useLocation()
  const { isAuthenticated, user, logout } = useAuthStore()
  const { data: unreadCount } = useUnreadCount()
  const [menuOpen, setMenuOpen] = useState(false)

  return (
    <>
      {/* 헤더 바 */}
      <header className="sticky top-0 z-50 bg-white border-b border-gray-100">
        <div className="flex items-center justify-between h-13 px-4">
          {/* 로고 */}
          <Link to="/" className="flex items-center">
            <Logo size="sm" />
          </Link>

          {/* 우측 아이콘 */}
          <div className="flex items-center gap-1">
            {/* 알림 */}
            <Link to="/mypage/notifications" className="relative p-2 rounded-lg">
              <Bell size={20} className="text-gray-600" />
              {unreadCount != null && unreadCount > 0 && (
                <span className="absolute top-1 right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-0.5 text-[9px] font-bold text-white">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </Link>

            {/* 햄버거 메뉴 */}
            <button onClick={() => setMenuOpen(true)} className="p-2 rounded-lg" aria-label="메뉴 열기">
              <Menu size={22} className="text-gray-700" />
            </button>
          </div>
        </div>
      </header>

      {/* 슬라이드 메뉴 */}
      <AnimatePresence>
        {menuOpen && (
          <>
            {/* 오버레이 */}
            <motion.div
              className="fixed inset-0 z-[60] bg-black/40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMenuOpen(false)}
            />

            {/* 메뉴 패널 */}
            <motion.div
              className="fixed top-0 right-0 z-[70] h-full w-[300px] bg-white shadow-2xl flex flex-col"
              initial={{ x: 300 }}
              animate={{ x: 0 }}
              exit={{ x: 300 }}
              transition={{ type: 'tween', duration: 0.25 }}
            >
              {/* 메뉴 헤더 */}
              <div className="flex items-center justify-between px-5 h-14 border-b border-gray-100">
                <span className="text-base font-bold text-gray-900">메뉴</span>
                <button onClick={() => setMenuOpen(false)} className="p-1" aria-label="메뉴 닫기">
                  <X size={22} className="text-gray-500" />
                </button>
              </div>

              {/* 사용자 정보 */}
              <div className="px-5 py-4 border-b border-gray-100">
                {isAuthenticated && user ? (
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-cb-primary/10">
                      <User size={18} className="text-cb-primary" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-gray-900">{user.name}님</p>
                      <p className="text-xs text-gray-400">{user.email}</p>
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <button
                      onClick={() => { setMenuOpen(false); navigate(ROUTES.LOGIN) }}
                      className="flex-1 rounded-lg bg-cb-primary py-2.5 text-sm font-semibold text-white"
                    >
                      로그인
                    </button>
                    <button
                      onClick={() => { setMenuOpen(false); navigate(ROUTES.SIGNUP) }}
                      className="flex-1 rounded-lg border border-gray-200 py-2.5 text-sm font-semibold text-gray-700"
                    >
                      회원가입
                    </button>
                  </div>
                )}
              </div>

              {/* 메뉴 항목 */}
              <div className="flex-1 overflow-y-auto py-3">
                <div className="px-5 mb-2">
                  <p className="text-[11px] font-bold text-gray-400 uppercase">서비스</p>
                </div>
                {[
                  { icon: Search, label: '시설 검색', to: ROUTES.FACILITIES },
                  { icon: Calculator, label: '통합돌봄계산기', to: '/care-calculator' },
                  { icon: Sparkles, label: 'AI 맞춤 추천', to: ROUTES.RECOMMEND },
                  { icon: Calculator, label: '비용 계산기', to: ROUTES.COST_SIMULATOR },
                  { icon: ClipboardCheck, label: '등급 자가진단', to: ROUTES.SELF_ASSESSMENT },
                  { icon: ListChecks, label: '시설 체크리스트', to: ROUTES.CHECKLIST },
                  { icon: Heart, label: '돌봄일지', to: ROUTES.CARE_LOG },
                  { icon: MessageSquare, label: '커뮤니티', to: '/community' },
                  { icon: Newspaper, label: '블로그', to: '/blog' },
                ].map((item) => (
                  <button
                    key={item.to}
                    onClick={() => { setMenuOpen(false); navigate(item.to) }}
                    className={`flex w-full items-center gap-3 px-5 py-3 text-left transition-colors ${
                      location.pathname === item.to ? 'bg-cb-primary/5 text-cb-primary' : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <item.icon size={18} />
                    <span className="text-sm font-medium">{item.label}</span>
                  </button>
                ))}

                <div className="px-5 mt-4 mb-2">
                  <p className="text-[11px] font-bold text-gray-400 uppercase">요양 가이드</p>
                </div>
                {[
                  { icon: BookOpen, label: '요양등급이란?', to: '/guide/care-grade' },
                  { icon: Building2, label: '시설 종류와 차이점', to: '/guide/facility-types' },
                  { icon: Calculator, label: '요양 비용 가이드', to: '/guide/cost' },
                  { icon: ExternalLink, label: '정부/공공기관 안내', to: '/guide/resources' },
                ].map((item) => (
                  <button
                    key={item.to}
                    onClick={() => { setMenuOpen(false); navigate(item.to) }}
                    className="flex w-full items-center gap-3 px-5 py-3 text-left text-gray-600 hover:bg-gray-50 transition-colors"
                  >
                    <item.icon size={16} />
                    <span className="text-sm">{item.label}</span>
                  </button>
                ))}

                {isAuthenticated && (
                  <>
                    <div className="px-5 mt-4 mb-2">
                      <p className="text-[11px] font-bold text-gray-400 uppercase">내 정보</p>
                    </div>
                    <button
                      onClick={() => { setMenuOpen(false); navigate(ROUTES.MYPAGE) }}
                      className="flex w-full items-center gap-3 px-5 py-3 text-left text-gray-700 hover:bg-gray-50"
                    >
                      <User size={16} />
                      <span className="text-sm font-medium">마이페이지</span>
                    </button>
                  </>
                )}

                {/* 시설 운영자 진입점 */}
                <div className="px-5 mt-4 mb-2">
                  <p className="text-[11px] font-bold text-gray-400 uppercase">시설 운영자</p>
                </div>
                <a
                  href="https://biz.carebalance.co.kr"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setMenuOpen(false)}
                  className="flex w-full items-center gap-3 px-5 py-3 text-left text-gray-700 hover:bg-gray-50"
                >
                  <Building2 size={16} />
                  <span className="text-sm font-medium">시설관리자</span>
                  <ExternalLink size={12} className="text-gray-400 ml-auto" />
                </a>
              </div>

              {/* 하단 */}
              {isAuthenticated && (
                <div className="border-t border-gray-100 px-5 py-3">
                  <button
                    onClick={() => { setMenuOpen(false); logout(); navigate(ROUTES.HOME) }}
                    className="flex w-full items-center gap-2 text-sm text-gray-400 hover:text-gray-600"
                  >
                    <LogOut size={16} />
                    로그아웃
                  </button>
                </div>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  )
}
